import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, MessageCircle, ShieldCheck, TrendingUp, Target, BarChart3,
  Play, Users, CheckCircle2, Eye, ThumbsUp, Zap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import PublicLayout from "@/components/layout/PublicLayout";
import SEO from "@/components/SEO";
import LeadForm from "@/components/LeadForm";
import { Reveal, Icon } from "@/components/motion";
import { api } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { waLink, trackEvent } from "@/lib/whatsapp";

export default function Home() {
  const { settings } = useSettings();
  const [services, setServices] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
  }, []);

  const hp = settings?.homepage || {};
  const wa = settings?.whatsapp || {};
  const brand = settings?.brand?.name || "NewStarAds";

  const schema = {
    "@context": "https://schema.org", "@type": "Organization", name: brand,
    url: "https://newstarads.com", description: settings?.seo?.default_description,
    contactPoint: { "@type": "ContactPoint", telephone: settings?.contact?.phone, contactType: "sales" },
  };

  return (
    <PublicLayout>
      <SEO schema={schema} />

      {/* HERO */}
      <section className="relative overflow-hidden hero-grid">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-white pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-14 pb-14 lg:pt-28 lg:pb-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <motion.div
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-1.5 text-xs font-semibold text-slate-600"
            >
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              Compliant YouTube promotion · No bots · No fake engagement
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.05 }}
              className="mt-6 font-display font-black text-4xl sm:text-5xl lg:text-7xl tracking-tighter leading-[1.02] sm:leading-[0.95]"
            >
              {hp.hero_headline || "Grow Your YouTube Reach With Professional Promotion"}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
              className="mt-6 text-base sm:text-lg text-slate-600 max-w-xl leading-relaxed"
            >
              {hp.hero_subheadline}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 }}
              className="mt-8 flex flex-col sm:flex-row gap-3"
            >
              <Link to="/consultation">
                <Button size="lg" className="rounded-full px-7 h-13 text-base w-full sm:w-auto" data-testid="hero-primary-cta">
                  {hp.primary_cta || "Get a Free Campaign Consultation"} <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
              <Link to="/services">
                <Button size="lg" variant="outline" className="rounded-full px-7 h-13 text-base w-full sm:w-auto" data-testid="hero-secondary-cta">
                  {hp.secondary_cta || "View Services"}
                </Button>
              </Link>
            </motion.div>

            <div className="mt-6">
              <a href={waLink(wa.number, wa.default_message)} target="_blank" rel="noreferrer"
                 onClick={() => trackEvent("whatsapp_click", { location: "hero" })}
                 className="inline-flex items-center gap-2 text-green-600 font-medium text-sm hover:text-green-700" data-testid="hero-whatsapp">
                <MessageCircle className="w-4 h-4" /> Talk to an expert on WhatsApp
              </a>
            </div>

            <p className="mt-8 text-sm text-slate-500 flex items-start gap-2 max-w-md">
              <ShieldCheck className="w-4 h-4 text-primary mt-0.5 shrink-0" />
              {hp.trust_line}
            </p>
          </div>

          {/* Dashboard preview */}
          <div className="lg:col-span-6">
            <DashboardPreview />
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon: ShieldCheck, t: "Policy-compliant", d: "Every campaign follows YouTube rules" },
            { icon: Eye, t: "Real audiences", d: "No bots or artificial traffic" },
            { icon: BarChart3, t: "Transparent reporting", d: "Only real, connected data" },
            { icon: Users, t: "Creator-focused", d: "Built for genuine growth" },
          ].map((x, i) => (
            <Reveal key={i} delay={i * 0.05} className="flex items-start gap-3">
              <x.icon className="w-5 h-5 text-primary shrink-0 mt-0.5" strokeWidth={1.5} />
              <div><p className="font-semibold text-sm">{x.t}</p><p className="text-xs text-slate-500">{x.d}</p></div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
        <Reveal className="max-w-2xl">
          <p className="text-xs uppercase tracking-widest font-semibold text-primary">What we do</p>
          <h2 className="mt-3 font-display text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight">Professional YouTube marketing services</h2>
          <p className="mt-4 text-slate-600 text-lg">From paid promotion to organic SEO — a complete, transparent toolkit for growing your channel the right way.</p>
        </Reveal>

        <div className="mt-12 grid md:grid-cols-12 gap-6">
          {services.slice(0, 8).map((s, i) => {
            const span = i === 0 ? "md:col-span-6" : i === 1 ? "md:col-span-6" : "md:col-span-4";
            return (
              <Reveal key={s.slug} delay={(i % 3) * 0.05} className={span}>
                <Link to={`/services/${s.slug}`} data-testid={`home-service-${s.slug}`}
                  className="group block h-full rounded-2xl border border-slate-200 bg-white p-7 transition-all hover:-translate-y-1 hover:shadow-xl hover:shadow-slate-200/60 hover:border-primary/30">
                  <div className="w-11 h-11 grid place-items-center rounded-xl bg-primary/10 text-primary mb-5">
                    <Icon name={s.icon} className="w-6 h-6" />
                  </div>
                  <h3 className="font-display text-xl font-bold tracking-tight">{s.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.short}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                    Learn more <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-slate-950 text-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
          <Reveal className="max-w-2xl">
            <p className="text-xs uppercase tracking-widest font-semibold text-primary">Transparent process</p>
            <h2 className="mt-3 font-display text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight">How a campaign works</h2>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-4 gap-6">
            {[
              { n: "01", icon: MessageCircle, t: "Free consultation", d: "We learn about your channel, goals and budget — honestly." },
              { n: "02", icon: Target, t: "Strategy & targeting", d: "We build a tailored plan and precise audience segments." },
              { n: "03", icon: Zap, t: "Launch & optimise", d: "Campaign goes live on compliant surfaces and we refine it." },
              { n: "04", icon: BarChart3, t: "Real reporting", d: "You see actual, connected data — never fabricated numbers." },
            ].map((x, i) => (
              <Reveal key={i} delay={i * 0.06} className="rounded-2xl border border-white/10 bg-white/5 p-6">
                <div className="flex items-center justify-between">
                  <x.icon className="w-6 h-6 text-primary" strokeWidth={1.5} />
                  <span className="font-display font-black text-3xl text-white/10">{x.n}</span>
                </div>
                <h3 className="mt-4 font-display text-lg font-bold">{x.t}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{x.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS (only if present) */}
      {testimonials.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
          <Reveal><h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">What clients say</h2></Reveal>
          <div className="mt-10 grid md:grid-cols-2 gap-6">
            {testimonials.map((t, i) => (
              <Reveal key={t.id} delay={i * 0.05} className="rounded-2xl border border-slate-200 bg-white p-7">
                <div className="flex gap-1 text-amber-400 mb-3">{Array.from({ length: t.rating || 5 }).map((_, k) => <ThumbsUp key={k} className="w-4 h-4 fill-current" />)}</div>
                <p className="text-slate-700 leading-relaxed">"{t.quote}"</p>
                <p className="mt-4 font-semibold text-sm">{t.name} <span className="text-slate-400 font-normal">· {t.role}</span></p>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* LEAD CTA */}
      <section id="consult" className="bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 grid lg:grid-cols-2 gap-14 items-start">
          <Reveal>
            <p className="text-xs uppercase tracking-widest font-semibold text-primary">Free consultation</p>
            <h2 className="mt-3 font-display text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight">Plan your YouTube campaign</h2>
            <p className="mt-4 text-slate-600 text-lg">Tell us about your channel and goals. We'll reply with honest, tailored advice — no pressure, no guaranteed-view gimmicks.</p>
            <ul className="mt-8 space-y-3">
              {["No obligation, no spam", "Real strategy from real people", "Transparent about what's realistic", "Reply on WhatsApp or email"].map((x) => (
                <li key={x} className="flex items-center gap-2 text-slate-700"><CheckCircle2 className="w-5 h-5 text-green-500" /> {x}</li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.1} className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm">
            <LeadForm />
          </Reveal>
        </div>
      </section>
    </PublicLayout>
  );
}

function DashboardPreview() {
  const bars = [40, 62, 55, 78, 70, 92, 85];
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.2 }}
      className="relative rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-slate-300/40 overflow-hidden"
      data-testid="hero-dashboard"
    >
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 bg-slate-50">
        <span className="w-3 h-3 rounded-full bg-red-400" /><span className="w-3 h-3 rounded-full bg-amber-400" /><span className="w-3 h-3 rounded-full bg-green-400" />
        <span className="ml-3 text-xs text-slate-400 font-medium">newstarads.com/dashboard</span>
      </div>
      <div className="p-5">
        <div className="grid grid-cols-3 gap-3">
          {[{ l: "Reach", v: "128.4K", i: Eye }, { l: "Watch time", v: "9,214h", i: Play }, { l: "Engagement", v: "6.8%", i: TrendingUp }].map((k) => (
            <div key={k.l} className="rounded-xl border border-slate-100 p-3">
              <k.i className="w-4 h-4 text-primary" strokeWidth={1.5} />
              <p className="mt-2 font-display font-bold text-lg tracking-tight">{k.v}</p>
              <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">{k.l}</p>
            </div>
          ))}
        </div>
        <div className="mt-4 rounded-xl border border-slate-100 p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold text-slate-600">Campaign performance</p>
            <span className="text-[10px] text-slate-400">Illustrative UI</span>
          </div>
          <div className="flex items-end gap-2 h-28">
            {bars.map((h, i) => (
              <motion.div key={i} initial={{ height: 0 }} animate={{ height: `${h}%` }} transition={{ duration: 0.6, delay: 0.4 + i * 0.05 }}
                className="flex-1 rounded-t-md bg-gradient-to-t from-primary/70 to-primary" />
            ))}
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2 text-[11px] text-slate-400">
          <ShieldCheck className="w-3.5 h-3.5" /> Sample layout — real dashboards show only your connected analytics data.
        </div>
      </div>
    </motion.div>
  );
}
